import { useState } from 'react';
import { Plus, Search, MoreHorizontal, Eye, Edit, Trash, CheckCircle, DollarSign, Send } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { formatCurrency, formatDate } from '@/lib/utils';
import { 
  PayRun, 
  PayRunStatus,
  PAY_RUN_STATUSES,
} from '@/types';
import { 
  usePayRuns, 
  useDeletePayRun, 
  useApprovePayRun, 
  useMarkPaid 
} from '@/hooks/usePayRuns';
import { usePrepareSTPSubmission } from '@/hooks/useSTP';
import { useToast } from '@/components/ui/Toast';
import { Link } from 'react-router-dom';

const getStatusBadge = (status: PayRunStatus) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
    draft: 'secondary',
    pending_approval: 'warning',
    approved: 'info',
    paid: 'success',
    cancelled: 'destructive',
  };
  const config = PAY_RUN_STATUSES.find((s) => s.value === status);
  return <Badge variant={variants[status] || 'default'}>{config?.label || status}</Badge>;
};

export function PayRunsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PayRunStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  const { toast } = useToast();
  const { data: payRunsData, isLoading } = usePayRuns({
    search: searchQuery,
    status: statusFilter === 'all' ? undefined : statusFilter,
    page: currentPage,
    limit: pageSize,
  });
  
  const deletePayRun = useDeletePayRun();
  const approvePayRun = useApprovePayRun();
  const markPaid = useMarkPaid();
  const submitToATO = usePrepareSTPSubmission();

  const payRuns = payRunsData?.data || [];
  const totalItems = payRunsData?.meta?.total || 0;

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this pay run?')) return;
    
    try {
      await deletePayRun.mutateAsync(id);
      toast({
        title: 'Success',
        description: 'Pay run deleted successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete pay run',
        variant: 'destructive',
      });
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await approvePayRun.mutateAsync(id);
      toast({
        title: 'Success',
        description: 'Pay run approved successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to approve pay run',
        variant: 'destructive',
      });
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await markPaid.mutateAsync(id);
      toast({
        title: 'Success',
        description: 'Pay run marked as paid successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to mark pay run as paid',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitSTP = async (id: string) => {
    try {
      await submitToATO.mutateAsync(id);
      toast({
        title: 'Success',
        description: 'STP submission sent to ATO successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit STP',
        variant: 'destructive',
      });
    }
  };

  const columns: Column<PayRun>[] = [
    {
      key: 'payRunNumber',
      header: 'Pay Run #',
      sortable: true,
      cell: (row) => (
        <Link 
          to={`/payroll/pay-runs/${row.id}`}
          className="font-medium text-aus-blue hover:underline"
        >
          {row.payRunNumber}
        </Link>
      ),
    },
    {
      key: 'period',
      header: 'Period',
      sortable: true,
      cell: (row) => (
        <span>
          {formatDate(row.periodStartDate)} - {formatDate(row.periodEndDate)}
        </span>
      ),
    },
    {
      key: 'paymentDate',
      header: 'Payment Date',
      sortable: true,
      cell: (row) => formatDate(row.paymentDate),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      cell: (row) => getStatusBadge(row.status),
    },
    {
      key: 'employeeCount',
      header: 'Employees',
      sortable: true,
      cell: (row) => row.employeeCount,
    },
    {
      key: 'totalNetPay',
      header: 'Total',
      sortable: true,
      cell: (row) => formatCurrency(row.totalNetPay),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/payroll/pay-runs/${row.id}`} className="flex items-center cursor-pointer">
                <Eye className="mr-2 h-4 w-4" />
                View
              </Link>
            </DropdownMenuItem>
            {row.status === 'draft' && (
              <>
                <DropdownMenuItem asChild>
                  <Link to={`/payroll/pay-runs/${row.id}?edit=true`} className="flex items-center cursor-pointer">
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="flex items-center cursor-pointer"
                  onClick={() => handleApprove(row.id)}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve
                </DropdownMenuItem>
              </>
            )}
            {row.status === 'approved' && (
              <>
                <DropdownMenuItem 
                  className="flex items-center cursor-pointer"
                  onClick={() => handleMarkPaid(row.id)}
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  Mark as Paid
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="flex items-center cursor-pointer"
                  onClick={() => handleSubmitSTP(row.id)}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Submit STP
                </DropdownMenuItem>
              </>
            )}
            {row.status === 'draft' && (
              <DropdownMenuItem 
                className="text-destructive focus:text-destructive flex items-center cursor-pointer"
                onClick={() => handleDelete(row.id)}
              >
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Calculate summary stats
  const draftTotal = payRuns
    .filter(p => p.status === 'draft')
    .reduce((sum, p) => sum + p.totalNetPay, 0);
  const approvedTotal = payRuns
    .filter(p => p.status === 'approved')
    .reduce((sum, p) => sum + p.totalNetPay, 0);
  const paidTotal = payRuns
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + p.totalNetPay, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pay Runs"
        description="Manage payroll and employee payments"
        action={{
          label: 'Create Pay Run',
          onClick: () => window.location.href = '/payroll/pay-runs/new',
          icon: <Plus className="h-4 w-4" />,
        }}
      />

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Draft Pay Runs</p>
          <p className="text-2xl font-bold">{formatCurrency(draftTotal)}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Approved</p>
          <p className="text-2xl font-bold text-info">{formatCurrency(approvedTotal)}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Paid (YTD)</p>
          <p className="text-2xl font-bold text-success">{formatCurrency(paidTotal)}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Total Pay Runs</p>
          <p className="text-2xl font-bold">{totalItems}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search pay runs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as PayRunStatus | 'all')}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {PAY_RUN_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <DataTable
        data={payRuns}
        columns={columns}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        emptyMessage="No pay runs found. Create your first pay run to get started."
      />
    </div>
  );
}
